import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@course-authoring/shared-types';
import { uploadImage, uploadImageBlob } from '../api/imageUploadApi';
import {
  completeImageLibraryUpload,
  imageLibraryKeys,
  prepareImageLibraryUpload,
  updateImageLibraryItem,
} from '../api/imageLibraryApi';
import { generateAltText } from '../api/aiAltTextApi';
import type { SelectedImageData, ImageInsertData } from './imageInsertTypes';
import { ModalFooter } from './ModalFooter';

interface AltTextStepProps {
  selectedImage: SelectedImageData;
  /** Header-context library scope: null = Emtrain (base), else the account id. */
  scopeAccountId: number | null;
  /** Display name for the account scope; null in the Emtrain context. */
  scopeAccountName: string | null;
  onInsert: (data: ImageInsertData) => void;
  onBack: () => void;
  onClose: () => void;
}

export function AltTextStep({
  selectedImage,
  scopeAccountId,
  scopeAccountName,
  onInsert,
  onBack,
  onClose,
}: AltTextStepProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const queryClient = useQueryClient();

  const [altText, setAltText] = useState(selectedImage.suggestedAlt);
  const [altTextError, setAltTextError] = useState<string | null>(null);
  const [isAiGenerated, setIsAiGenerated] = useState(selectedImage.aiGeneratedAlt);
  const [isGeneratingAlt, setIsGeneratingAlt] = useState(false);
  const [label, setLabel] = useState(selectedImage.libraryLabel ?? '');
  const [saveToLibrary, setSaveToLibrary] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const autoGeneratedRef = useRef(false);
  const isExistingLibraryImage = Boolean(selectedImage.libraryItemId);
  const initialAltTextRef = useRef(selectedImage.suggestedAlt);
  const initialLabelRef = useRef(selectedImage.libraryLabel ?? '');

  // Auto-generate alt text on arrival when no existing alt text
  useEffect(() => {
    if (autoGeneratedRef.current) return;
    if (selectedImage.aiGeneratedAlt) return; // already has AI alt text
    if (selectedImage.suggestedAlt) return; // already has alt text from saved image

    autoGeneratedRef.current = true;
    triggerAltTextGeneration();
  }, []);  

  const triggerAltTextGeneration = useCallback(() => {
    if (!selectedImage.previewUrl) return;

    const imageData = selectedImage.previewUrl.startsWith('data:')
      ? selectedImage.previewUrl.split(',')[1] ?? ''
      : '';

    if (!imageData) return;

    setIsGeneratingAlt(true);
    generateAltText(imageData, selectedImage.mimeType)
      .then((generatedAlt) => {
        setAltText(generatedAlt);
        setIsAiGenerated(true);
      })
      .catch(() => {
        // Keep the existing alt text on failure
      })
      .finally(() => {
        setIsGeneratingAlt(false);
      });
  }, [selectedImage.previewUrl, selectedImage.mimeType]);

  const handleAltTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAltText(e.target.value);
    setAltTextError(null);
    if (isAiGenerated) {
      setIsAiGenerated(false);
    }
  }, [isAiGenerated]);

  const handleLabelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
  }, []);

  const handleSaveToLibraryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isExistingLibraryImage) {
      setSaveToLibrary(e.target.checked);
    }
  }, [isExistingLibraryImage]);

  // Prevent Delete/Backspace from propagating to modal (which might close it)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.stopPropagation();
    }
  }, []);

  const handleInsert = useCallback(async () => {
    const trimmedAlt = altText.trim();
    if (!trimmedAlt) {
      setAltTextError('Alt text is required');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      let finalUrl = selectedImage.previewUrl;
      let finalKey = selectedImage.s3Key;
      let finalStoragePrefix = selectedImage.storagePrefix;
      let finalLibraryItemId = selectedImage.libraryItemId;
      let libraryMutated = false;
      const trimmedLabel = label.trim();
      const originalFilename = selectedImage.file?.name || selectedImage.libraryFilename || `image-${Date.now()}`;

      if (selectedImage.needsUpload) {
        const prepared = saveToLibrary
          ? await prepareImageLibraryUpload({
            externalAccountId: scopeAccountId,
            originalFilename,
            contentType: selectedImage.mimeType,
            altText: trimmedAlt,
            label: trimmedLabel || null,
          })
          : null;
        const uploadOptions = prepared
          ? { storagePrefix: prepared.storagePrefix }
          : undefined;
        if (selectedImage.file) {
          const uploaded = await uploadImage(selectedImage.file, uploadOptions);
          finalUrl = uploaded.url;
          finalKey = uploaded.key;
          finalStoragePrefix = uploaded.storagePrefix;
        } else if (selectedImage.blob) {
          const filename = `image-${Date.now()}.${selectedImage.mimeType.split('/')[1] || 'png'}`;
          const uploaded = await uploadImageBlob(selectedImage.blob, filename, selectedImage.mimeType, uploadOptions);
          finalUrl = uploaded.url;
          finalKey = uploaded.key;
          finalStoragePrefix = uploaded.storagePrefix;
        }
        if (prepared) {
          if (!finalKey) throw new Error('Image upload did not return library storage metadata');
          const completed = await completeImageLibraryUpload(prepared.id, {
            s3Key: finalKey,
            publicUrl: finalUrl,
            fileSizeBytes: selectedImage.file?.size ?? selectedImage.blob?.size ?? null,
          });
          finalLibraryItemId = completed.id;
          finalUrl = completed.publicUrl;
          finalKey = completed.s3Key;
          finalStoragePrefix = completed.storagePrefix;
          libraryMutated = true;
        }
      }

      if (isExistingLibraryImage && selectedImage.libraryItemId) {
        const changedAlt = trimmedAlt !== initialAltTextRef.current;
        const changedLabel = trimmedLabel !== initialLabelRef.current;
        if (changedAlt || changedLabel) {
          const updated = await updateImageLibraryItem(selectedImage.libraryItemId, {
            altText: trimmedAlt,
            label: trimmedLabel || null,
          });
          finalLibraryItemId = updated.id;
          finalUrl = updated.publicUrl;
          finalKey = updated.s3Key;
          finalStoragePrefix = updated.storagePrefix;
          libraryMutated = true;
        }
      } else if (saveToLibrary && !finalLibraryItemId) {
        if (!finalKey || !finalStoragePrefix) {
          throw new Error('Image upload did not return library storage metadata');
        }
        const prepared = await prepareImageLibraryUpload({
          externalAccountId: scopeAccountId,
          contentType: selectedImage.mimeType,
          originalFilename,
          altText: trimmedAlt,
          label: trimmedLabel || null,
        });
        const completed = await completeImageLibraryUpload(prepared.id, {
          s3Key: finalKey,
          publicUrl: finalUrl,
          fileSizeBytes: selectedImage.file?.size ?? selectedImage.blob?.size ?? null,
        });
        finalLibraryItemId = completed.id;
        finalUrl = completed.publicUrl;
        finalKey = completed.s3Key;
        finalStoragePrefix = completed.storagePrefix;
        libraryMutated = true;
      }

      // The page's library queries cache for the stale window; refresh them so
      // a modal save is visible on the Image Library page immediately.
      if (libraryMutated) {
        void queryClient.invalidateQueries({ queryKey: imageLibraryKeys.lists() });
        if (finalLibraryItemId) {
          void queryClient.invalidateQueries({ queryKey: imageLibraryKeys.detail(finalLibraryItemId) });
        }
      }

      onInsert({
        src: finalUrl,
        alt: trimmedAlt,
        label: saveToLibrary ? trimmedLabel || undefined : undefined,
        saveToLibrary,
        libraryItemId: finalLibraryItemId,
        s3Key: finalKey,
        storagePrefix: finalStoragePrefix,
      });
      onClose();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
      setIsUploading(false);
    }
  }, [altText, selectedImage, onInsert, onClose, saveToLibrary, label, isExistingLibraryImage, scopeAccountId, queryClient]);

  const isBusy = isUploading || isGeneratingAlt;

  return (
    <>
      <div style={styles.content}>
        <div style={styles.previewContainer}>
          <img
            src={selectedImage.previewUrl}
            alt="Selected image preview"
            style={styles.previewImage}
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="alt-text-input">
            <span style={styles.labelRow}>
              <span>Alt Text <span style={styles.required}>*</span></span>
              {isAiGenerated && (
                <span style={styles.aiBadge} aria-label="AI Generated">
                  AI Generated
                </span>
              )}
              {isGeneratingAlt ? (
                <span style={styles.generatingHint} aria-live="polite" role="status">
                  Generating...
                </span>
              ) : (
                <button
                  type="button"
                  style={styles.generateAltButton}
                  onClick={triggerAltTextGeneration}
                  disabled={isBusy}
                  aria-label="Generate alt text with AI"
                >
                  Generate with AI
                </button>
              )}
            </span>
          </label>
          <textarea
            id="alt-text-input"
            value={altText}
            onChange={handleAltTextChange}
            onKeyDown={handleKeyDown}
            placeholder="Describe the image for accessibility"
            style={{
              ...styles.textarea,
              ...(altTextError ? styles.textareaError : {}),
            }}
            rows={3}
            disabled={isBusy}
            aria-invalid={Boolean(altTextError)}
            aria-describedby={altTextError ? 'alt-text-error' : undefined}
          />
          {altTextError ? (
            <p id="alt-text-error" style={styles.error} role="alert">
              {altTextError}
            </p>
          ) : (
            <p style={styles.hint}>
              Required for accessibility. Describe what's shown, not what it means.
            </p>
          )}
        </div>

        <div style={styles.checkboxGroup}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={saveToLibrary}
              onChange={handleSaveToLibraryChange}
              style={styles.checkbox}
              aria-describedby="save-to-library-hint"
              disabled={isBusy || isExistingLibraryImage}
            />
            <span style={styles.checkboxText}>
              Save to {scopeAccountName ?? 'Emtrain'} Image Library
            </span>
          </label>
        </div>

        {saveToLibrary && (
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="label-input">
              Label (optional)
            </label>
            <input
              id="label-input"
              type="text"
              value={label}
              onChange={handleLabelChange}
              onKeyDown={handleKeyDown}
              placeholder="e.g., Team meeting photo"
              style={styles.input}
              aria-describedby="label-hint"
            />
            <p id="label-hint" style={styles.hint}>
              Not shown to learners — used to search and organize your image library
            </p>
          </div>
        )}

        {isExistingLibraryImage && (
          <div style={styles.infoBanner} role="note">
            Changes to alt text or label will update this image's record in the Image Library.
          </div>
        )}

        {uploadError && (
          <p style={styles.error} role="alert" aria-live="polite">
            {uploadError}
          </p>
        )}
      </div>

      <ModalFooter
        onCancel={onBack}
        cancelLabel="Back"
        cancelDisabled={isBusy}
        actions={[
          {
            label: isUploading ? 'Inserting...' : 'Insert Image',
            onClick: handleInsert,
            disabled: isBusy || !altText.trim(),
            ariaLabel: isUploading ? 'Uploading image' : 'Insert image with alt text',
            ariaBusy: isUploading,
            variant: 'primary',
          },
        ]}
      />
    </>
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, React.CSSProperties> {
  return {
    content: {
      padding: '20px',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      flex: 1,
    },
    previewContainer: {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: tokens.backgrounds.app,
      borderRadius: '8px',
      border: `1px solid ${tokens.text.subtle}`,
      padding: '12px',
    },
    previewImage: {
      maxWidth: '100%',
      maxHeight: '200px',
      objectFit: 'contain',
      borderRadius: '4px',
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    label: {
      fontSize: '14px',
      fontWeight: 500,
      color: tokens.text.primary,
    },
    labelRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    required: {
      color: tokens.ui.errorText,
    },
    aiBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: 600,
      borderRadius: '10px',
      backgroundColor: `color-mix(in srgb, ${tokens.ui.selectionBorder} 15%, transparent)`,
      color: tokens.ui.selectionBorder,
    },
    generatingHint: {
      fontSize: '12px',
      color: tokens.text.subtle,
      fontStyle: 'italic',
    },
    generateAltButton: {
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: 600,
      border: `1px solid ${tokens.ui.selectionBorder}`,
      borderRadius: '10px',
      background: 'none',
      color: tokens.ui.selectionBorder,
      cursor: 'pointer',
    },
    textarea: {
      padding: '10px 12px',
      fontSize: '14px',
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      outline: 'none',
      fontFamily: 'inherit',
      resize: 'vertical',
      minHeight: '60px',
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
    },
    textareaError: {
      borderColor: tokens.ui.errorBorder,
    },
    input: {
      padding: '10px 12px',
      fontSize: '14px',
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      outline: 'none',
      fontFamily: 'inherit',
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
    },
    error: {
      color: tokens.ui.errorText,
      fontSize: '13px',
      margin: 0,
    },
    hint: {
      fontSize: '12px',
      color: tokens.text.subtle,
      margin: 0,
    },
    checkboxGroup: {
      display: 'flex',
      alignItems: 'center',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      color: tokens.text.primary,
      cursor: 'pointer',
    },
    checkbox: {
      margin: 0,
    },
    checkboxText: {
      fontWeight: 500,
    },
    infoBanner: {
      padding: '10px 12px',
      borderRadius: '6px',
      border: `1px solid ${tokens.ui.selectionBorder}`,
      backgroundColor: `color-mix(in srgb, ${tokens.ui.selectionBorder} 12%, transparent)`,
      color: tokens.text.primary,
      fontSize: '13px',
    },
  };
}
